import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutData } from '@/mocks/about';
import SEOHead from '@/components/base/SEOHead';

const canonicalUrl = 'http://zaixinjiaju.com/about';
const seoData = {
  title: '关于我们_韩漫在线 - 韩国漫画免费阅读平台',
  description: '了解韩漫在线 - 韩国漫画免费阅读平台，成立于2023年，专注为中文读者提供最新、最全、最高清的韩漫资源，已累计服务超过500万读者。商务合作联系：hello@zaixinjiaju.com',
  keywords: '韩漫在线,关于我们,韩国漫画,免费漫画,在线漫画,商务合作',
  canonicalUrl,
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': 'http://zaixinjiaju.com/#organization',
      'name': '韩漫在线',
      'url': 'http://zaixinjiaju.com/',
      'description': '韩漫在线 - 韩国漫画免费阅读平台，成立于2023年，专注为中文读者提供最新、最全、最高清的韩漫资源。',
      'foundingDate': '2023-03',
      'contactPoint': {
        '@type': 'ContactPoint',
        'email': 'hello@zaixinjiaju.com',
        'contactType': 'customer service',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://zaixinjiaju.com/' },
        { '@type': 'ListItem', 'position': 2, 'name': '关于我们', 'item': canonicalUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${canonicalUrl}#webpage`,
      'url': canonicalUrl,
      'name': '关于我们 - 韩漫在线',
      'description': '了解韩漫在线 - 韩国漫画免费阅读平台',
      'isPartOf': { '@id': 'http://zaixinjiaju.com/#website' },
      'about': { '@id': 'http://zaixinjiaju.com/#organization' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': aboutData.faq.map((item) => ({
        '@type': 'Question',
        'name': item.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': item.answer,
        },
      })),
    },
  ],
};

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const honeypotValue = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim() || '';
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('company_alt');

      const response = await fetch('http://zaixinjiaju.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const breadcrumbs = [
    { label: '首页', href: '/' },
    { label: '关于我们', href: '/about' },
  ];

  return (
    <>
      <SEOHead {...seoData} />
      <Navbar />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="relative w-full overflow-hidden bg-background-50">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20artistic%20illustration%20with%20soft%20coral%20amber%20and%20cream%20gradient%20tones%2C%20subtle%20geometric%20wave%20patterns%2C%20Korean%20manhwa%20inspired%20elegant%20flowing%20lines%2C%20minimalist%20modern%20art%20composition%2C%20peaceful%20and%20sophisticated%20atmosphere%2C%20gentle%20light%20diffusion%20effect%2C%20clean%20aesthetic%20background%20suitable%20for%20brand%20story%20page&width=1600&height=560&seq=about-hero-bg-01&orientation=landscape"
            alt=""
            className="w-full h-72 md:h-80 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-background-50" />
          <div className="relative -mt-32 md:-mt-40">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap" aria-label="面包屑导航">
                {breadcrumbs.map((crumb, idx) => (
                  <span key={crumb.href} className="flex items-center gap-2">
                    {idx > 0 && <span className="text-foreground-300">/</span>}
                    {idx < breadcrumbs.length - 1 ? (
                      <Link to={crumb.href} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="text-foreground-950 font-medium whitespace-nowrap">{crumb.label}</span>
                    )}
                  </span>
                ))}
              </nav>
              <div className="max-w-3xl bg-background-50 rounded-2xl p-6 md:p-10">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  {aboutData.hero.title}
                </h1>
                <p className="text-lg text-foreground-600 font-heading mb-3">
                  {aboutData.hero.subtitle}
                </p>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {aboutData.hero.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="text-center group">
                  <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${stat.icon} text-xl`} />
                  </div>
                  <span className="block text-3xl md:text-4xl font-heading font-bold text-foreground-950 whitespace-nowrap">
                    {stat.value}
                  </span>
                  <span className="text-sm text-foreground-500 mt-1 whitespace-nowrap">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission + Values */}
        <section className="w-full bg-background-100 py-14 md:py-18">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Artistic%20illustration%20of%20diverse%20people%20reading%20digital%20comics%20on%20devices%20together%2C%20warm%20cozy%20atmosphere%2C%20Korean%20manhwa%20inspired%20soft%20art%20style%2C%20coral%20and%20amber%20color%20palette%2C%20modern%20minimalist%20composition%2C%20friendly%20community%20vibe%2C%20clean%20elegant%20aesthetic%2C%20no%20visible%20text&width=700&height=500&seq=about-mission-img-01&orientation=landscape"
                  alt="韩漫在线 - 我们的使命"
                  title="韩漫在线 - 我们的使命"
                  className="w-full h-72 md:h-80 object-cover rounded-2xl"
                />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-4">
                  {aboutData.mission.title}
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  {aboutData.mission.content}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {aboutData.values.map((value) => (
                    <div key={value.title} className="bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-200 transition-colors duration-200">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                        <i className={`${value.icon} text-lg`} />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1 whitespace-nowrap">{value.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{value.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="w-full bg-background-50 py-14 md:py-18">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2" />
              <div className="flex flex-col gap-8">
                {aboutData.timeline.map((item, idx) => (
                  <div key={item.year} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-accent-500 rounded-full -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-50" />
                    <div className="md:w-1/2 ml-10 md:ml-0 md:px-8">
                      <div className={`bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-accent-200 transition-colors duration-200 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <span className="text-xs font-bold text-accent-600 whitespace-nowrap">{item.year}</span>
                        <h3 className="text-sm font-semibold text-foreground-950 mt-1 whitespace-nowrap">{item.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:block md:w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Business Cooperation */}
        <section className="w-full bg-background-100 py-14 md:py-18" id="business">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 text-center mb-2">
              {aboutData.business.title}
            </h2>
            <p className="text-sm text-foreground-500 text-center max-w-xl mx-auto mb-10 leading-relaxed">
              {aboutData.business.description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-12">
              {aboutData.business.items.map((item) => (
                <div key={item.title} className="bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-300 transition-all duration-200 text-center group">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${item.icon} text-lg`} />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-background-200/70">
                <div className="p-6 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-mail-line text-lg" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">商务邮箱</h3>
                  <p className="text-xs text-foreground-500 whitespace-nowrap">{aboutData.business.email}</p>
                </div>
                <div className="p-6 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-wechat-line text-lg" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">商务微信</h3>
                  <p className="text-xs text-foreground-500 whitespace-nowrap">{aboutData.business.wechat}</p>
                </div>
                <div className="p-6 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-phone-line text-lg" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">联系电话</h3>
                  <p className="text-xs text-foreground-500 whitespace-nowrap">{aboutData.business.phone}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full bg-background-100 py-14 md:py-18" id="faq">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              常见问题
            </h2>
            <div className="flex flex-col gap-3" itemScope itemType="https://schema.org/FAQPage">
              {aboutData.faq.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-foreground-950 pr-4" itemProp="name">
                      {item.question}
                    </span>
                    <i className={`text-lg text-foreground-400 transition-transform duration-300 flex-shrink-0 ${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <p className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed" itemProp="text">
                      {item.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full bg-background-50 py-14 md:py-18" id="contact">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 text-center mb-2">
              {aboutData.contact.title}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              {aboutData.contact.description}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="bg-background-100 rounded-xl p-5 text-center border border-background-200/70 hover:border-primary-200 transition-colors duration-200">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg" />
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">邮箱</h3>
                <p className="text-xs text-foreground-500 whitespace-nowrap">{aboutData.contact.email}</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center border border-background-200/70 hover:border-primary-200 transition-colors duration-200">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-wechat-line text-lg" />
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">微信</h3>
                <p className="text-xs text-foreground-500 whitespace-nowrap">{aboutData.contact.wechat}</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center border border-background-200/70 hover:border-primary-200 transition-colors duration-200">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-time-line text-lg" />
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">工作时间</h3>
                <p className="text-xs text-foreground-500 whitespace-nowrap">{aboutData.contact.workingHours}</p>
              </div>
            </div>

            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6 text-center">
                发送消息
              </h3>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-lg mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-xs text-foreground-600 mb-1.5">主题</label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1.5">消息内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的消息内容（最多500字符）"
                  />
                </div>
                <div style={{ position: 'absolute', opacity: '0', pointerEvents: 'none', height: '0', width: '0', overflow: 'hidden' }}>
                  <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 bg-foreground-950 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'loading' ? '发送中...' : '发送消息'}
                </button>
                {formStatus === 'success' && (
                  <p className="text-center text-sm text-green-600">消息发送成功！我们会尽快回复您。</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-center text-sm text-red-500">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}